import { useState } from 'react';
import { Settings as SettingsIcon, Download, Trash2, AlertCircle } from 'lucide-react';

const STORAGE_PREFIX = 'bmw.stack.';

function CardShell({ children }) {
  return (
    <div className="rounded-2xl bg-stone-900/60 border border-stone-800 p-6 hover:border-amber-500/40 transition-colors">
      {children}
    </div>
  );
}

function getAllLocalData() {
  const data = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) {
      try {
        data[key.slice(STORAGE_PREFIX.length)] = JSON.parse(localStorage.getItem(key));
      } catch {
        data[key.slice(STORAGE_PREFIX.length)] = localStorage.getItem(key);
      }
    }
  }
  return data;
}

function exportJson() {
  const data = getAllLocalData();
  const blob = new Blob(
    [JSON.stringify({ exportedAt: new Date().toISOString(), ...data }, null, 2)],
    { type: 'application/json' },
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `bmore-stack-export-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function clearAllData() {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
  }
  keys.forEach(k => localStorage.removeItem(k));
  window.location.reload();
}

export default function Settings() {
  const [confirmReset, setConfirmReset] = useState(false);

  const dataKeys = (() => {
    const count = { items: 0, bytes: 0 };
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        count.items++;
        count.bytes += (localStorage.getItem(key) || '').length * 2; // UTF-16
      }
    }
    return count;
  })();

  return (
    <CardShell>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center">
          <SettingsIcon size={20} className="text-stone-900" />
        </div>
        <h3 className="font-display text-lg font-semibold text-cream">Settings</h3>
      </div>

      {/* Data summary */}
      <div className="rounded-lg bg-stone-950/40 p-3 mb-4">
        <div className="text-xs text-stone-500 mb-1">Your local data</div>
        <div className="text-sm text-stone-300 font-mono tabular-nums">
          {dataKeys.items} keys · {(dataKeys.bytes / 1024).toFixed(1)} KB
        </div>
        <div className="text-[10px] text-stone-600 mt-1">
          Stored in this browser only. Nothing is sent to any server.
        </div>
      </div>

      {/* Export */}
      <button
        onClick={exportJson}
        className="w-full rounded-lg bg-stone-800/60 border border-stone-700 py-2.5 px-4 text-sm text-stone-300 font-medium hover:border-stone-600 hover:text-cream transition flex items-center justify-center gap-2 mb-2"
      >
        <Download size={14} />
        <span>Export my data (JSON)</span>
      </button>

      {/* Reset */}
      {!confirmReset ? (
        <button
          onClick={() => setConfirmReset(true)}
          className="w-full rounded-lg bg-stone-800/60 border border-stone-700 py-2.5 px-4 text-sm text-stone-400 font-medium hover:border-red-500/40 hover:text-red-400 transition flex items-center justify-center gap-2"
        >
          <Trash2 size={14} />
          <span>Reset all data</span>
        </button>
      ) : (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
          <div className="flex items-start gap-2 mb-3">
            <AlertCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-red-300/90">
              This will permanently delete your stack, streak, goals, and DCA settings
              from this browser. This cannot be undone.
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={clearAllData}
              className="flex-1 rounded-lg bg-red-500/20 border border-red-500/40 py-2 text-xs font-semibold text-red-400 hover:bg-red-500/30 transition"
            >
              Confirm reset
            </button>
            <button
              onClick={() => setConfirmReset(false)}
              className="flex-1 rounded-lg bg-stone-800/60 border border-stone-700 py-2 text-xs font-medium text-stone-400 hover:text-cream transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <p className="text-[10px] text-stone-600 mt-3 leading-relaxed">
        When auth ships in a future version, you'll be able to "claim" this local data
        into your account with one click. Until then, export regularly if you care about
        cross-device or browser-crash recovery.
      </p>
    </CardShell>
  );
}
